import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Bug,
  ShieldAlert,
  Paperclip,
  Sparkles,
  GitBranch,
  CalendarClock,
  AlertTriangle,
} from 'lucide-react'
import {
  useGetPlatformBoard,
  useGetPlatformRequest,
  useGetPlatformPipeline,
  useGetPlatformAssetUrl,
  useInterpretPlatformRequest,
  useConfirmPlatformTriage,
  useAcceptPlatformRequest,
  useResolvePlatformRequest,
  useDeclinePlatformRequest,
  useSavePlatformSlaBand,
  useUpdatePipelineItem,
  useSetPlatformRequestLinks,
} from '../hooks/backend/platform'
import { useGetTaskLinkOptions } from '../hooks/backend/tasks'
import { LinkCell } from '../components/tasks/TaskCells'
import type { LinkKind, TaskLink, TaskLinkOption } from '../utils/tasks'
import { useCapabilities } from '../hooks/useCapabilities'
import { Spinner } from '../lib/shadcn/spinner'
import { Button } from '../lib/shadcn/button'
import { Badge } from '../lib/shadcn/badge'
import { Input } from '../lib/shadcn/input'
import { Textarea } from '../lib/shadcn/textarea'
import { Label } from '../lib/shadcn/label'
import { NativeSelect } from '../lib/shadcn/native-select'
import { Switch } from '../lib/shadcn/switch'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../lib/shadcn/tabs'
import { toast } from '../lib/shadcn/sonner'
import { cn } from '../lib/shadcn/utils'
import { writeErrorMessage } from '../utils/writeError'
import {
  KINDS,
  canCalibrate,
  clockLabel,
  fileSize,
  groupByStatus,
  promiseLabel,
  proposalNote,
  readingDisagreements,
  type Band,
  type PlatformRequest,
  type Proposal,
} from '../utils/platform'

/**
 * Platform Requests: what is broken, what has been asked for, and when each of
 * them is promised.
 *
 * WHAT THIS SCREEN IS FOR. Every problem with this platform used to arrive as a
 * WhatsApp message and leave no record, so nobody could say how many there were,
 * which ones were still open, or how long any of them had taken. This page is the
 * record. Reports come in through the sidebar button on whatever screen the
 * person was standing on, the model reads them and asks up to five questions,
 * and a person turns the answer into one dev plan item and one task.
 *
 * THE PROMISED DATES ON THIS PAGE SAY "PROVISIONAL", AND THAT IS THE MOST
 * IMPORTANT THING ON IT.
 *
 * Measured on 2 September 2026, before any of this was built: the task board held
 * 520 tasks and 347 marked complete, and 200 of those 347 have a completion date
 * EARLIER than their creation date, because they came in from monday.com and
 * created_at is the import date. Strip the machine actors and the tasks a human
 * created here and finished here number 12. Leduma had created 2 and finished 0.
 *
 * So there was no cycle-time history to calibrate a promise against, and the five
 * bands shipped as a judgement rather than a measurement. The Promised dates tab
 * shows the measured median and 90th percentile accumulating beside each band and
 * the count behind it, and the word "provisional" stays on every date until
 * somebody replaces a guess with a number and says so in a note.
 *
 * THE MODEL PROPOSES AND A PERSON DECIDES. platform_request_policy
 * .model_may_triage_alone is false, so the reading is recorded in model_reading
 * and confirmed here. Both are kept, so where they disagree the page can say
 * which field the model got wrong, and over enough reports that list is the only
 * evidence anybody will have about whether the reading is worth trusting.
 *
 * WHAT THIS PAGE CANNOT FIX. Intake was never the constraint. Two people close
 * this queue, and a board that accepts faster than they close produces a visible
 * backlog rather than a smaller one. The Pipeline tab therefore leads with effort
 * days per person against what has been promised, because that gap is the number
 * the whole thing exists to stop hiding.
 */

type BoardFeed = {
  requests: PlatformRequest[]
  bands: Band[]
  proposal: Proposal[]
  policy: { max_clarifying_questions: number; triage_target_hours: number } | null
  assignees: Array<{ email: string; display_name: string | null }>
  phases: Array<{ phase_key: string; label: string; sort_order: number }>
  can_triage: boolean
  is_admin: boolean
  email: string | null
}

type PipelineItem = {
  ref: string
  phase_key: string
  phase_label: string | null
  title: string
  status: string
  planned_start: string | null
  planned_end: string | null
  actual_completion: string | null
  effort_days: number | null
  owner_name: string | null
  blocked_reason: string | null
  request_ref: number | null
  severity: string | null
  promise_by: string | null
  is_overdue: boolean
}

type PipelineFeed = {
  items: PipelineItem[]
  phases: Array<{ phase_key: string; label: string; sort_order: number }>
  load: Array<{ owner_name: string; items: number; effort_days: number | null; overdue: number }>
  can_edit: boolean
}

type DetailFeed = {
  request: PlatformRequest
  questions: Array<{
    id: string
    seq: number
    question: string
    answer: string | null
    answered_by_name: string | null
  }>
  assets: Array<{
    id: string
    filename: string
    mime_type: string | null
    size_bytes: number | null
    uploaded_by: string
  }>
  links: {
    vehicle: TaskLink[]
    owner: TaskLink[]
    driver: TaskLink[]
  }
  can_triage: boolean
}

const TONE_VARIANT = {
  ok: 'success',
  warn: 'warning',
  bad: 'destructive',
  muted: 'outline',
} as const

function ClockBadge({ r }: { r: PlatformRequest }) {
  const { text, tone } = clockLabel(r)
  return <Badge variant={TONE_VARIANT[tone]}>{text}</Badge>
}

export default function PlatformRequests() {
  const { loading: accessLoading, canView, caption } = useCapabilities()

  const board = useGetPlatformBoard<BoardFeed>()
  const pipeline = useGetPlatformPipeline<PipelineFeed>()
  const detail = useGetPlatformRequest<DetailFeed>()
  const assetUrl = useGetPlatformAssetUrl<{ url: string }>()
  const interpret = useInterpretPlatformRequest()
  const confirmTriage = useConfirmPlatformTriage()
  const accept = useAcceptPlatformRequest()
  const resolve = useResolvePlatformRequest()
  const decline = useDeclinePlatformRequest()
  const saveBand = useSavePlatformSlaBand()
  const updateItem = useUpdatePipelineItem()
  const linkOptionsQuery = useGetTaskLinkOptions<TaskLinkOption[]>()
  const setLinks = useSetPlatformRequestLinks()

  const [showClosed, setShowClosed] = useState(false)
  const [openId, setOpenId] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const boardTrigger = board.trigger
  const pipelineTrigger = pipeline.trigger
  const detailTrigger = detail.trigger

  const loadBoard = useCallback(() => {
    boardTrigger({ include_closed: showClosed }, { skipCache: true })
  }, [boardTrigger, showClosed])

  const loadPipeline = useCallback(() => {
    pipelineTrigger({ include_done: false }, { skipCache: true })
  }, [pipelineTrigger])

  useEffect(() => {
    if (canView) loadBoard()
  }, [canView, loadBoard])

  useEffect(() => {
    if (canView) loadPipeline()
  }, [canView, loadPipeline])

  const linkOptionsTrigger = linkOptionsQuery.trigger
  useEffect(() => {
    if (canView) void linkOptionsTrigger({}, { skipCache: true })
  }, [canView, linkOptionsTrigger])

  useEffect(() => {
    if (openId) detailTrigger({ id: openId }, { skipCache: true })
  }, [openId, detailTrigger])

  const feed = board.data
  const rows = feed?.requests ?? []
  const canTriage = feed?.can_triage === true
  const linkOptions = (linkOptionsQuery.data as TaskLinkOption[] | undefined) ?? []

  const saveLinks = async (kind: LinkKind, ids: string[]) => {
    if (!openId) return
    await setLinks.trigger({ request_id: openId, kind, ids }, { skipCache: true })
    detailTrigger({ id: openId }, { skipCache: true })
  }
  const grouped = useMemo(() => groupByStatus(rows), [rows])

  const headline = useMemo(() => {
    const open = rows.filter((r) => r.status !== 'Resolved' && r.status !== 'Declined')
    return {
      open: open.length,
      overdue: open.filter((r) => r.is_overdue).length,
      unread: open.filter((r) => r.triage_overdue).length,
      provisional: (feed?.bands ?? []).filter((b) => b.provisional).length,
      bands: (feed?.bands ?? []).length,
    }
  }, [rows, feed])

  async function openAsset(id: string) {
    try {
      const res = (await assetUrl.trigger({ id }, { skipCache: true })) as { url?: string } | null
      if (res?.url) window.open(res.url, '_blank', 'noopener,noreferrer')
    } catch (err) {
      toast.error(writeErrorMessage(err))
    }
  }

  async function run(action: () => Promise<unknown>, done: string) {
    setBusy(true)
    try {
      await action()
      toast.success(done)
      loadBoard()
      loadPipeline()
      if (openId) detailTrigger({ id: openId }, { skipCache: true })
    } catch (err) {
      toast.error(writeErrorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  if (accessLoading) {
    return (
      <div className="flex h-full items-center justify-center gap-2 text-sm text-muted-foreground">
        <Spinner className="h-4 w-4" /> Checking access…
      </div>
    )
  }

  if (!canView) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <div className="flex max-w-md flex-col items-center gap-3 rounded-lg border border-border bg-card p-8 text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand-soft-foreground))]">
            <ShieldAlert className="h-6 w-6" />
          </div>
          <p className="text-sm font-medium text-foreground">
            Platform requests are available to signed-in Fleetly staff.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-full min-h-0 flex-col bg-card">
      <div className="border-b border-border px-4 py-3">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand-soft-foreground))]">
            <Bug className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <h1 className="text-base font-semibold">Platform Requests</h1>
            <p className="truncate text-xs text-muted-foreground">{caption}</p>
          </div>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
          <Badge variant="outline">{headline.open} open</Badge>
          {headline.overdue > 0 && (
            <Badge variant="destructive">{headline.overdue} past their date</Badge>
          )}
          {headline.unread > 0 && <Badge variant="warning">{headline.unread} not yet read</Badge>}
          {headline.provisional > 0 && (
            <Badge variant="outline">
              {headline.provisional} of {headline.bands} promised dates still provisional
            </Badge>
          )}
        </div>
      </div>

      <Tabs defaultValue="reports" className="flex min-h-0 flex-1 flex-col">
        <TabsList className="mx-4 mt-3 w-fit">
          <TabsTrigger value="reports">Reports</TabsTrigger>
          <TabsTrigger value="pipeline">Pipeline</TabsTrigger>
          <TabsTrigger value="dates">Promised dates</TabsTrigger>
        </TabsList>

        {/* ------------------------------------------------------------------ */}
        <TabsContent value="reports" className="min-h-0 flex-1 overflow-y-auto px-4 pb-6">
          <div className="flex items-center gap-2 py-3">
            <Switch id="closed" checked={showClosed} onCheckedChange={setShowClosed} />
            <Label htmlFor="closed" className="text-xs">
              Show closed and declined
            </Label>
            {board.loading && <Spinner className="h-3.5 w-3.5" />}
          </div>

          {board.error && (
            <p className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm">
              {board.error}
            </p>
          )}

          {!board.loading && rows.length === 0 && (
            <p className="py-8 text-center text-sm text-muted-foreground">
              Nothing reported yet. Use Report a problem in the menu, from whichever screen the
              problem is on.
            </p>
          )}

          {grouped.map(([status, group]) => (
            <section key={status} className="mb-6">
              <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {status} ({group.length})
              </h2>
              <ul className="space-y-2">
                {group.map((r) => (
                  <li
                    key={r.id}
                    className={cn(
                      'rounded-md border bg-background',
                      r.is_overdue ? 'border-destructive/50' : 'border-border',
                    )}
                  >
                    <button
                      type="button"
                      onClick={() => setOpenId(openId === r.id ? null : r.id)}
                      className="flex w-full items-start gap-3 px-3 py-2.5 text-left"
                    >
                      <span className="mt-0.5 shrink-0 text-xs tabular-nums text-muted-foreground">
                        #{r.ref}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-medium">
                          {r.title ?? r.reported_text}
                        </span>
                        <span className="mt-0.5 flex flex-wrap items-center gap-1.5 text-[11px] text-muted-foreground">
                          {r.kind && <Badge variant="outline">{r.kind}</Badge>}
                          {r.severity_label && <Badge variant="outline">{r.severity_label}</Badge>}
                          {r.area && <span>{r.area}</span>}
                          <span>
                            {r.reporter_name ?? r.reporter} on {r.reported_on}
                          </span>
                          {r.page_path && <span>from {r.page_path}</span>}
                          {r.source === 'meeting' && <Badge variant="outline">from a meeting</Badge>}
                          {r.asset_count > 0 && (
                            <span className="inline-flex items-center gap-1">
                              <Paperclip className="h-3 w-3" />
                              {r.asset_count}
                            </span>
                          )}
                          {r.questions_asked > 0 && (
                            <span>
                              {r.questions_answered} of {r.questions_asked} answered
                            </span>
                          )}
                          {r.dev_plan_ref && <Badge variant="outline">{r.dev_plan_ref}</Badge>}
                          {r.assignee_name && <span>{r.assignee_name}</span>}
                        </span>
                      </span>
                      <span className="flex shrink-0 flex-col items-end gap-1">
                        <ClockBadge r={r} />
                        <span className="text-[11px] text-muted-foreground">
                          {promiseLabel(r)}
                        </span>
                      </span>
                    </button>

                    {openId === r.id && (
                      <RequestPanel
                        row={r}
                        data={detail.data}
                        loading={detail.loading}
                        canTriage={canTriage}
                        busy={busy}
                        bands={feed?.bands ?? []}
                        phases={feed?.phases ?? []}
                        assignees={feed?.assignees ?? []}
                        linkOptions={linkOptions}
                        onSaveLinks={saveLinks}
                        onOpenAsset={openAsset}
                        onInterpret={() =>
                          run(
                            () => interpret.trigger({ id: r.id }, { skipCache: true }),
                            'Read.',
                          )
                        }
                        onTriage={(p) =>
                          run(
                            () => confirmTriage.trigger({ id: r.id, ...p }, { skipCache: true }),
                            'Triaged, and a date is set.',
                          )
                        }
                        onAccept={(p) =>
                          run(
                            () => accept.trigger({ id: r.id, ...p }, { skipCache: true }),
                            'Accepted. A pipeline item and a task now exist.',
                          )
                        }
                        onResolve={(resolution) =>
                          run(
                            () =>
                              resolve.trigger({ id: r.id, resolution }, { skipCache: true }),
                            'Closed.',
                          )
                        }
                        onDecline={(reason) =>
                          run(
                            () => decline.trigger({ id: r.id, reason }, { skipCache: true }),
                            'Declined, with the reason on record.',
                          )
                        }
                      />
                    )}
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </TabsContent>

        {/* ------------------------------------------------------------------ */}
        <TabsContent value="pipeline" className="min-h-0 flex-1 overflow-y-auto px-4 pb-6">
          <PipelinePanel
            feed={pipeline.data}
            loading={pipeline.loading}
            error={pipeline.error}
            busy={busy}
            onSetStatus={(p) =>
              run(() => updateItem.trigger(p, { skipCache: true }), 'Pipeline updated.')
            }
          />
        </TabsContent>

        {/* ------------------------------------------------------------------ */}
        <TabsContent value="dates" className="min-h-0 flex-1 overflow-y-auto px-4 pb-6">
          <DatesPanel
            bands={feed?.bands ?? []}
            proposal={feed?.proposal ?? []}
            policy={feed?.policy ?? null}
            canTriage={canTriage}
            busy={busy}
            onSave={(p) =>
              run(() => saveBand.trigger(p, { skipCache: true }), 'Band changed, with the reason recorded.')
            }
          />
        </TabsContent>
      </Tabs>
    </div>
  )
}

// ---------------------------------------------------------------------------

function RequestPanel({
  row,
  data,
  loading,
  canTriage,
  busy,
  bands,
  phases,
  assignees,
  linkOptions,
  onSaveLinks,
  onOpenAsset,
  onInterpret,
  onTriage,
  onAccept,
  onResolve,
  onDecline,
}: {
  row: PlatformRequest
  data: DetailFeed | null
  loading: boolean
  canTriage: boolean
  busy: boolean
  bands: Band[]
  phases: Array<{ phase_key: string; label: string }>
  assignees: Array<{ email: string; display_name: string | null }>
  linkOptions: TaskLinkOption[]
  onSaveLinks: (kind: LinkKind, ids: string[]) => Promise<void>
  onOpenAsset: (id: string) => void
  onInterpret: () => void
  onTriage: (p: Record<string, unknown>) => void
  onAccept: (p: Record<string, unknown>) => void
  onResolve: (resolution: string) => void
  onDecline: (reason: string) => void
}) {
  const reading = row.model_reading
  const [kind, setKind] = useState(reading?.kind ?? row.kind ?? 'bug')
  const [area, setArea] = useState(reading?.area ?? row.area ?? '')
  const [severity, setSeverity] = useState(reading?.severity ?? row.severity ?? '')
  const [title, setTitle] = useState(reading?.title ?? row.title ?? '')
  const [diagnosis, setDiagnosis] = useState(reading?.diagnosis ?? row.diagnosis ?? '')
  const [cause, setCause] = useState(reading?.suspected_cause ?? row.suspected_cause ?? '')
  const [effort, setEffort] = useState(
    String(reading?.effort_days ?? row.proposed_effort_days ?? ''),
  )
  const [assignee, setAssignee] = useState(row.assignee_email ?? assignees[0]?.email ?? '')
  const [phase, setPhase] = useState('now')
  const [resolution, setResolution] = useState('')
  const [reason, setReason] = useState('')

  const disagreements = readingDisagreements(row)
  const band = bands.find((b) => b.severity === severity) ?? null

  return (
    <div className="space-y-4 border-t border-border px-3 py-3 text-sm">
      <div>
        <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
          In their own words
        </p>
        <p className="mt-1 whitespace-pre-wrap">{row.reported_text}</p>
      </div>

      {/* Optional, and usually empty. Most reports are about a broken screen
          or a missing feature and name no vehicle, owner or driver at all;
          this is here for the reports that do. See db/migrations/0025 for why
          three real foreign keys beat one polymorphic id. */}
      <div>
        <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Linked to</p>
        <div className="mt-1 flex flex-wrap gap-3">
          <LinkCell
            kinds={['vehicle']}
            links={{ vehicle: data?.links?.vehicle ?? [] }}
            options={linkOptions}
            save={onSaveLinks}
            disabled={!canTriage}
            title="Vehicles"
          />
          <LinkCell
            kinds={['owner']}
            links={{ owner: data?.links?.owner ?? [] }}
            options={linkOptions}
            save={onSaveLinks}
            disabled={!canTriage}
            title="Vehicle owners"
          />
          <LinkCell
            kinds={['driver']}
            links={{ driver: data?.links?.driver ?? [] }}
            options={linkOptions}
            save={onSaveLinks}
            disabled={!canTriage}
            title="Drivers"
          />
        </div>
      </div>

      {loading && <Spinner className="h-3.5 w-3.5" />}

      {(data?.assets?.length ?? 0) > 0 && (
        <div>
          <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Screenshots</p>
          <ul className="mt-1 space-y-1">
            {data!.assets.map((a) => (
              <li key={a.id}>
                <button
                  type="button"
                  onClick={() => onOpenAsset(a.id)}
                  className="inline-flex items-center gap-2 text-xs text-[hsl(var(--brand))] hover:underline"
                >
                  <Paperclip className="h-3 w-3" /> {a.filename}
                  <span className="text-muted-foreground">{fileSize(a.size_bytes)}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {(data?.questions?.length ?? 0) > 0 && (
        <div>
          <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
            Questions and answers
          </p>
          <ul className="mt-1 space-y-2">
            {data!.questions.map((q) => (
              <li key={q.id} className="rounded border border-border px-2 py-1.5 text-xs">
                <span className="block font-medium">{q.question}</span>
                <span className={q.answer ? '' : 'text-muted-foreground'}>
                  {q.answer ?? 'Not answered yet'}
                  {q.answered_by_name ? ` (${q.answered_by_name})` : ''}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {reading && (
        <div className="rounded-md border border-border bg-card px-3 py-2">
          <p className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-muted-foreground">
            <Sparkles className="h-3 w-3" /> Read by {row.model_name ?? 'the model'}, confidence{' '}
            {reading.confidence ?? 'unknown'}
          </p>
          <p className="mt-1 text-xs">{reading.diagnosis ?? 'No diagnosis returned.'}</p>
          {reading.suspected_cause && (
            <p className="mt-1 text-xs text-muted-foreground">
              Suspected cause: {reading.suspected_cause}
            </p>
          )}
          {reading.attachment_problems && reading.attachment_problems.length > 0 && (
            <p className="mt-1 flex items-start gap-1 text-xs text-amber-700 dark:text-amber-400">
              <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
              {reading.attachment_problems.join(' ')} It was working partly blind.
            </p>
          )}
          {/* The disagreement is shown rather than hidden. Over enough reports
              this is the only evidence about whether the reading is worth
              trusting, and a page that quietly overwrote it would throw that
              away. */}
          {disagreements.length > 0 && (
            <p className="mt-1 text-xs text-muted-foreground">
              A person changed {disagreements.join('; ')}.
            </p>
          )}
        </div>
      )}

      {row.status === 'Resolved' && (
        <p className="rounded-md border border-border bg-card px-3 py-2 text-xs">
          Closed by {row.resolved_by} on {row.resolved_at?.slice(0, 10)}: {row.resolution}
        </p>
      )}
      {row.status === 'Declined' && (
        <p className="rounded-md border border-border bg-card px-3 py-2 text-xs">
          Declined by {row.declined_by}: {row.decline_reason}
        </p>
      )}

      {!canTriage && row.status !== 'Resolved' && row.status !== 'Declined' && (
        <p className="text-xs text-muted-foreground">
          Triaging and accepting is done by whoever holds the platform pipeline capability. Your
          report is on the board and nothing else is needed from you.
        </p>
      )}

      {canTriage && (row.status === 'Clarifying' || row.status === 'Awaiting triage') && (
        <div className="space-y-3 rounded-md border border-dashed border-border px-3 py-3">
          <div className="flex flex-wrap items-center gap-2">
            <Button type="button" size="sm" variant="outline" onClick={onInterpret} disabled={busy}>
              <Sparkles className="mr-2 h-3.5 w-3.5" />
              {reading ? 'Read it again' : 'Have it read'}
            </Button>
            <span className="text-[11px] text-muted-foreground">
              The reading is a proposal. Whatever you submit below is what is recorded, and it is
              what sets the date.
            </span>
          </div>

          <div className="grid gap-2 sm:grid-cols-2">
            <div>
              <Label className="text-xs">What it is</Label>
              <NativeSelect value={kind} onChange={(e) => setKind(e.target.value)}>
                {KINDS.map((k) => (
                  <option key={k} value={k}>
                    {k}
                  </option>
                ))}
              </NativeSelect>
            </div>
            <div>
              <Label className="text-xs">How bad</Label>
              <NativeSelect value={severity} onChange={(e) => setSeverity(e.target.value)}>
                <option value="">Choose</option>
                {bands.map((b) => (
                  <option key={b.severity} value={b.severity}>
                    {b.label}, {b.days_to_fix} days{b.provisional ? ' (provisional)' : ''}
                  </option>
                ))}
              </NativeSelect>
            </div>
            <div>
              <Label className="text-xs">Area</Label>
              <Input value={area} onChange={(e) => setArea(e.target.value)} />
            </div>
            <div>
              <Label className="text-xs">Rough size in days</Label>
              <Input
                type="number"
                step="0.25"
                min="0.25"
                value={effort}
                onChange={(e) => setEffort(e.target.value)}
              />
            </div>
          </div>
          <div>
            <Label className="text-xs">Title</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} />
          </div>
          <div>
            <Label className="text-xs">What is actually wrong</Label>
            <Textarea rows={3} value={diagnosis} onChange={(e) => setDiagnosis(e.target.value)} />
          </div>
          <div>
            <Label className="text-xs">Suspected cause, if there is evidence for one</Label>
            <Input value={cause} onChange={(e) => setCause(e.target.value)} />
          </div>

          {band && (
            <p className="text-[11px] text-muted-foreground">
              {band.label} promises {band.days_to_fix} working days from the day it was reported,
              which was {row.reported_on}.
              {band.provisional
                ? ' That number is provisional: it has never been measured.'
                : ` Measured from ${band.calibrated_from_n} closed reports.`}
            </p>
          )}

          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              size="sm"
              disabled={busy}
              onClick={() =>
                onTriage({
                  kind,
                  area: area || null,
                  severity,
                  title,
                  diagnosis: diagnosis || null,
                  suspected_cause: cause || null,
                  effort_days: effort ? Number(effort) : null,
                })
              }
            >
              Triage and set the date
            </Button>
            <div className="flex flex-1 items-center gap-2">
              <Input
                placeholder="Reason, if you are declining it"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={busy}
                onClick={() => onDecline(reason)}
              >
                Decline
              </Button>
            </div>
          </div>
        </div>
      )}

      {canTriage && row.status === 'Triaged' && (
        <div className="space-y-3 rounded-md border border-dashed border-border px-3 py-3">
          <p className="text-xs text-muted-foreground">
            Accepting writes one pipeline item and one task, both due {row.promise_by}. The task
            carries a target date you set, so under the Sin Offering rules it is a commitment that
            counts.
          </p>
          <div className="grid gap-2 sm:grid-cols-3">
            <div>
              <Label className="text-xs">Who is doing it</Label>
              <NativeSelect value={assignee} onChange={(e) => setAssignee(e.target.value)}>
                {assignees.map((a) => (
                  <option key={a.email} value={a.email}>
                    {a.display_name ?? a.email}
                  </option>
                ))}
              </NativeSelect>
            </div>
            <div>
              <Label className="text-xs">Phase</Label>
              <NativeSelect value={phase} onChange={(e) => setPhase(e.target.value)}>
                {phases.map((p) => (
                  <option key={p.phase_key} value={p.phase_key}>
                    {p.label}
                  </option>
                ))}
              </NativeSelect>
            </div>
            <div>
              <Label className="text-xs">Effort days</Label>
              <Input
                type="number"
                step="0.25"
                min="0.25"
                value={effort}
                onChange={(e) => setEffort(e.target.value)}
              />
            </div>
          </div>
          <Button
            type="button"
            size="sm"
            disabled={busy}
            onClick={() =>
              onAccept({
                assignee_email: assignee,
                phase_key: phase,
                effort_days: effort ? Number(effort) : null,
              })
            }
          >
            Accept into the pipeline
          </Button>
        </div>
      )}

      {canTriage && (row.status === 'Triaged' || row.status === 'Accepted') && (
        <div className="flex items-center gap-2">
          <Input
            placeholder="What was done about it"
            value={resolution}
            onChange={(e) => setResolution(e.target.value)}
          />
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={busy}
            onClick={() => onResolve(resolution)}
          >
            Close it
          </Button>
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------

function PipelinePanel({
  feed,
  loading,
  error,
  busy,
  onSetStatus,
}: {
  feed: PipelineFeed | null
  loading: boolean
  error: string | null
  busy: boolean
  onSetStatus: (p: Record<string, unknown>) => void
}) {
  const [openRef, setOpenRef] = useState<string | null>(null)
  const [status, setStatus] = useState('in_progress')
  const [reason, setReason] = useState('')
  const [completed, setCompleted] = useState('')

  const items = feed?.items ?? []
  const load = feed?.load ?? []

  return (
    <div className="space-y-5 py-3">
      {/* THE LOAD TABLE IS FIRST, AND THAT IS THE ARGUMENT OF THE WHOLE PAGE.
          Intake was never the constraint. Two people close this queue, and the
          number worth looking at before any individual item is how much has been
          promised against how much fits. */}
      <section>
        <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Open effort per person
        </h2>
        {load.length === 0 && !loading && (
          <p className="text-sm text-muted-foreground">Nothing open in the pipeline.</p>
        )}
        <ul className="flex flex-wrap gap-2">
          {load.map((l) => (
            <li
              key={l.owner_name}
              className="rounded-md border border-border bg-background px-3 py-2 text-sm"
            >
              <span className="block font-medium">{l.owner_name}</span>
              <span className="text-xs text-muted-foreground">
                {l.items} items,{' '}
                {l.effort_days === null ? 'effort not sized' : `${l.effort_days} days of effort`}
                {l.overdue > 0 ? `, ${l.overdue} past their date` : ''}
              </span>
            </li>
          ))}
        </ul>
      </section>

      <section>
        <h2 className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          <GitBranch className="h-3.5 w-3.5" /> The platform pipeline ({items.length} open)
        </h2>
        {error && (
          <p className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm">
            {error}
          </p>
        )}
        {loading && <Spinner className="h-3.5 w-3.5" />}
        <ul className="space-y-1.5">
          {items.map((i) => (
            <li
              key={i.ref}
              className={cn(
                'rounded-md border bg-background',
                i.is_overdue ? 'border-destructive/50' : 'border-border',
              )}
            >
              <button
                type="button"
                onClick={() => setOpenRef(openRef === i.ref ? null : i.ref)}
                className="flex w-full items-start gap-3 px-3 py-2 text-left"
              >
                <span className="shrink-0 text-xs font-medium tabular-nums">{i.ref}</span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm">{i.title}</span>
                  <span className="mt-0.5 flex flex-wrap gap-2 text-[11px] text-muted-foreground">
                    <Badge variant="outline">{i.status.replace(/_/g, ' ')}</Badge>
                    <span>{i.phase_label ?? i.phase_key}</span>
                    <span>{i.owner_name ?? 'Nobody'}</span>
                    <span>
                      {i.effort_days === null ? 'not sized' : `${i.effort_days} days`}
                    </span>
                    {i.planned_end && <span>due {i.planned_end}</span>}
                    {i.request_ref && <span>report #{i.request_ref}</span>}
                    {i.blocked_reason && <span>blocked: {i.blocked_reason}</span>}
                  </span>
                </span>
              </button>
              {openRef === i.ref && feed?.can_edit && (
                <div className="space-y-2 border-t border-border px-3 py-2">
                  <div className="grid gap-2 sm:grid-cols-3">
                    <div>
                      <Label className="text-xs">Status</Label>
                      <NativeSelect value={status} onChange={(e) => setStatus(e.target.value)}>
                        {[
                          'not_started',
                          'in_progress',
                          'blocked',
                          'awaiting_decision',
                          'on_hold',
                          'done',
                        ].map((s) => (
                          <option key={s} value={s}>
                            {s.replace(/_/g, ' ')}
                          </option>
                        ))}
                      </NativeSelect>
                    </div>
                    <div>
                      <Label className="text-xs">
                        {status === 'done' ? 'Date it was actually finished' : 'Finished on'}
                      </Label>
                      <Input
                        type="date"
                        value={completed}
                        onChange={(e) => setCompleted(e.target.value)}
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Reason, if blocked or on hold</Label>
                      <Input value={reason} onChange={(e) => setReason(e.target.value)} />
                    </div>
                  </div>
                  {status === 'done' && (
                    <p className="text-[11px] text-muted-foreground">
                      Give the real date. Ticking today for something finished last week puts an
                      error into every cycle-time figure the promised dates are measured from.
                    </p>
                  )}
                  <Button
                    type="button"
                    size="sm"
                    disabled={busy}
                    onClick={() =>
                      onSetStatus({
                        ref: i.ref,
                        status,
                        actual_completion: completed || null,
                        reason: reason || null,
                      })
                    }
                  >
                    Save
                  </Button>
                </div>
              )}
            </li>
          ))}
        </ul>
      </section>
    </div>
  )
}

// ---------------------------------------------------------------------------

function DatesPanel({
  bands,
  proposal,
  policy,
  canTriage,
  busy,
  onSave,
}: {
  bands: Band[]
  proposal: Proposal[]
  policy: { max_clarifying_questions: number; triage_target_hours: number } | null
  canTriage: boolean
  busy: boolean
  onSave: (p: Record<string, unknown>) => void
}) {
  const [editing, setEditing] = useState<string | null>(null)
  const [days, setDays] = useState('')
  const [note, setNote] = useState('')
  const [measured, setMeasured] = useState(false)
  const [n, setN] = useState('')

  return (
    <div className="space-y-5 py-3">
      <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-3 text-xs text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200">
        <p className="font-medium">Every one of these numbers started as a judgement.</p>
        <p className="mt-1">
          When this board was built there was no cycle-time history to calibrate against. The task
          board held 520 tasks and 347 marked complete, and 200 of those had a completion date
          earlier than their creation date because they were imported from monday.com. Twelve tasks
          created by a person here have ever been finished here. So the promised days below were
          chosen, not measured, and every date carries the word provisional until the measurement
          replaces the guess.
        </p>
      </div>

      {policy && (
        <p className="text-xs text-muted-foreground">
          A report is read and triaged inside {policy.triage_target_hours} hours, and the model may
          ask at most {policy.max_clarifying_questions} questions.
        </p>
      )}

      <ul className="space-y-2">
        {proposal.map((p) => {
          const band = bands.find((b) => b.severity === p.severity)
          return (
            <li key={p.severity} className="rounded-md border border-border bg-background px-3 py-2.5">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="flex items-center gap-2 text-sm font-medium">
                  <CalendarClock className="h-3.5 w-3.5 text-muted-foreground" />
                  {p.label}
                </span>
                <span className="flex items-center gap-2 text-xs">
                  <Badge variant={p.in_force_provisional ? 'warning' : 'success'}>
                    {p.in_force_days} working days
                    {p.in_force_provisional ? ', provisional' : ', measured'}
                  </Badge>
                  {canCalibrate(p) && <Badge variant="outline">ready to calibrate</Badge>}
                </span>
              </div>
              <p className="mt-1 text-xs text-muted-foreground">{proposalNote(p)}</p>
              {band?.note && (
                <p className="mt-1 text-[11px] text-muted-foreground">
                  Set {band.effective_from} by {band.set_by}: {band.note}
                </p>
              )}

              {canTriage && (
                <div className="mt-2">
                  {editing === p.severity ? (
                    <div className="space-y-2 rounded border border-dashed border-border px-2 py-2">
                      <div className="grid gap-2 sm:grid-cols-3">
                        <div>
                          <Label className="text-xs">Working days</Label>
                          <Input
                            type="number"
                            min="1"
                            value={days}
                            onChange={(e) => setDays(e.target.value)}
                          />
                        </div>
                        <div className="flex items-end gap-2">
                          <Switch
                            id={`m-${p.severity}`}
                            checked={measured}
                            onCheckedChange={setMeasured}
                          />
                          <Label htmlFor={`m-${p.severity}`} className="text-xs">
                            This is measured
                          </Label>
                        </div>
                        <div>
                          <Label className="text-xs">From how many closed reports</Label>
                          <Input
                            type="number"
                            min="0"
                            value={n}
                            onChange={(e) => setN(e.target.value)}
                          />
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs">Why it changed</Label>
                        <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
                      </div>
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          size="sm"
                          disabled={busy}
                          onClick={() => {
                            onSave({
                              severity: p.severity,
                              days_to_fix: Number(days),
                              provisional: !measured,
                              calibrated_from_n: n ? Number(n) : 0,
                              note,
                            })
                            setEditing(null)
                          }}
                        >
                          Save the new band
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() => setEditing(null)}
                        >
                          Cancel
                        </Button>
                      </div>
                      <p className="text-[11px] text-muted-foreground">
                        The current band is closed off today and a new row opens, so nothing already
                        promised is restated.
                      </p>
                    </div>
                  ) : (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setEditing(p.severity)
                        setDays(
                          canCalibrate(p)
                            ? String(p.measured_p90_days ?? p.in_force_days)
                            : String(p.in_force_days),
                        )
                        setMeasured(canCalibrate(p))
                        setN(String(p.n_closed))
                        setNote('')
                      }}
                    >
                      Change this band
                    </Button>
                  )}
                </div>
              )}
            </li>
          )
        })}
      </ul>
    </div>
  )
}
